import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSurveySubmit } from '../hooks/survey';

// Utility function to shuffle an array using Fisher-Yates algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// All waiting survey type strings. `pricing` is one of them: same carousel,
// same endpoint, same once-per-user gating off the answered-types list.
type WaitingSurveyType =
  | 'pricing'
  | 'role'
  | 'skill-level'
  | 'discovery'
  | 'genre'
  | 'online-community'
  | 'practice-frequency'
  | 'reading-method'
  | 'music-source'
  | 'use-case'
  | 'input-method';

interface SurveyOption {
  value: string;
  label: string;
  hasTextInput?: boolean;
}

interface GenericSurveyProps {
  title: string;
  question?: string;
  options: SurveyOption[];
  surveyType: WaitingSurveyType;
  textInputPlaceholder?: string;
  onSurveyAnswered?: () => void; // Callback when survey is answered/dismissed
  shuffleOptions?: boolean; // Whether to randomize the order of options
  autoSubmit?: boolean; // If true, submit immediately on option click (no Submit button)
  singleColumn?: boolean; // If true, options stack vertically in one column
  maxWidthClass?: string; // Tailwind max-width class for the survey box
  compact?: boolean; // If true, scales text/padding down on sm and smaller screens
}

const GenericSurvey = ({
  title,
  question,
  options,
  surveyType,
  textInputPlaceholder = 'Please specify...',
  onSurveyAnswered,
  shuffleOptions = false,
  autoSubmit = false,
  singleColumn = false,
  maxWidthClass = 'max-w-5xl',
  compact = false,
}: GenericSurveyProps) => {
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [textInput, setTextInput] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { submitWaitingSurvey, loading } = useSurveySubmit();

  // Shuffle options exactly once on mount — useState lazy init avoids re-shuffle on re-renders
  const [displayOptions] = useState<SurveyOption[]>(() =>
    shuffleOptions ? shuffleArray(options) : options
  );

  // `compact` shrinks the survey on sm and smaller screens (used by the
  // waiting-survey carousel), scaling back up to the default sizes at md+.
  const containerPadClass = compact ? 'p-4 md:p-6' : 'p-6';
  const titleClass = compact ? 'text-lg md:text-xl' : 'text-2xl';
  const questionClass = compact ? 'text-sm md:text-lg' : 'text-lg';
  const optionPadClass = compact ? 'p-2 md:p-3' : 'p-4';
  const optionTextClass = compact ? 'text-sm md:text-base' : 'text-lg';
  const radioSizeClass = compact ? 'h-4 w-4 md:h-5 md:w-5' : 'h-5 w-5';

  // Core submit logic that accepts the option value directly (needed for autoSubmit)
  const submitSurvey = useCallback(
    async (optionValue: string) => {
      if (!optionValue || loading) return;

      try {
        const optionObj = options.find((opt) => opt.value === optionValue);
        const selectedLabel = optionObj?.label || '';
        const hasText = optionObj?.hasTextInput;

        await submitWaitingSurvey({
          surveyType,
          selectedOption: optionValue,
          selectedLabel,
          dismissed: false,
        });

        console.log('Survey response submitted:', {
          surveyType,
          selectedOption: optionValue,
          ...(hasText && { textInput }),
        });
        setSubmitted(true);
      } catch (error) {
        console.error('Error submitting survey:', error);
        setSubmitted(true);
      }
    },
    [loading, options, surveyType, textInput, submitWaitingSurvey]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitSurvey(selectedOption);
  };

  const handleOptionChange = async (value: string) => {
    setSelectedOption(value);
    if (autoSubmit) {
      // Submit immediately — no need to wait for state update
      await submitSurvey(value);
    }
  };

  const handleClose = async () => {
    try {
      // Submit dismissal to backend
      await submitWaitingSurvey({
        surveyType,
        selectedOption: '',
        selectedLabel: '',
        dismissed: true,
      });
    } catch (error) {
      console.error('Error submitting survey dismissal:', error);
    } finally {
      setHidden(true);
      onSurveyAnswered?.();
    }
  };

  // Show "thank you" for 1 second, then hide and notify parent
  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        setHidden(true);
        onSurveyAnswered?.();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [submitted, onSurveyAnswered]);

  // Don't render anything if hidden
  if (hidden) {
    return null;
  }

  if (submitted) {
    return (
      <div
        className={`
          relative w-full max-w-5xl rounded-lg border border-solid
          border-gray-300 bg-white p-6 shadow-sm
        `}
      >
        <button
          onClick={handleClose}
          className={`
            absolute top-4 right-4 rounded-full p-1 text-gray-400
            transition-colors
            hover:bg-gray-100 hover:text-gray-600
            focus:ring-2 focus:ring-gray-300 focus:outline-none
          `}
          aria-label="Close survey"
          type="button"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <p className="text-center text-xl font-semibold text-green-600">
          Thank you for your feedback! 🎹
        </p>
      </div>
    );
  }

  // Check if current option requires text input and if it's filled
  const currentOptionHasTextInput = options.find(
    (opt) => opt.value === selectedOption
  )?.hasTextInput;
  const isSubmitDisabled =
    loading ||
    !selectedOption ||
    (currentOptionHasTextInput && !textInput.trim());

  return (
    <div
      className={`
        relative w-full ${maxWidthClass}
        ${containerPadClass}
        rounded-lg border border-solid border-gray-300 bg-white shadow-sm
      `}
    >
      <button
        onClick={handleClose}
        className={`
          absolute top-4 right-4 rounded-full p-1 text-gray-400
          transition-colors
          hover:bg-gray-100 hover:text-gray-600
          focus:ring-2 focus:ring-gray-300 focus:outline-none
        `}
        aria-label="Close survey"
        type="button"
      >
        <svg
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
      <h3 className={`mb-4 font-semibold text-gray-800 ${titleClass}`}>
        {title}
      </h3>
      <form onSubmit={handleSubmit}>
        {question && (
          <p className={`mb-4 text-gray-700 ${questionClass}`}>{question}</p>
        )}
        <div
          className={
            singleColumn
              ? 'mb-6 flex flex-col gap-3'
              : 'mb-6 flex flex-wrap gap-3'
          }
        >
          {displayOptions.map((option) => (
            <div
              key={option.value}
              className={singleColumn ? '' : 'min-w-[200px] flex-1'}
            >
              <label
                className={`
                  flex cursor-pointer items-center rounded-lg border
                  transition-colors
                  ${optionPadClass}
                  ${
                    selectedOption === option.value
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }
                `}
              >
                <input
                  type="radio"
                  name={`${surveyType}-option`}
                  value={option.value}
                  checked={selectedOption === option.value}
                  onChange={(e) => handleOptionChange(e.target.value)}
                  className={`
                    ${radioSizeClass}
                    cursor-pointer text-yellow-500
                    focus:ring-2 focus:ring-yellow-500
                  `}
                />
                <span className={`ml-3 text-gray-700 ${optionTextClass}`}>
                  {option.label}
                </span>
              </label>
              {option.hasTextInput && selectedOption === option.value && (
                <div className="mt-2">
                  <input
                    type="text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder={textInputPlaceholder}
                    className={`
                      w-full rounded-lg border border-gray-300 p-3 text-lg
                      focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500
                      focus:outline-none
                    `}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        {!autoSubmit && (
          <button
            type="submit"
            disabled={isSubmitDisabled}
            className={`
              focus:ring-opacity-50 focus:ring-2 focus:ring-yellow-500
              focus:outline-none
              mt-2 min-h-[44px] min-w-[44px] cursor-pointer rounded-lg border
              border-solid border-yellow-500 bg-yellow-500 px-8 py-3 text-xl
              font-semibold text-white transition-transform duration-100
              hover:bg-yellow-600
              active:scale-95 active:bg-yellow-700
              disabled:cursor-not-allowed disabled:opacity-50
              disabled:hover:bg-yellow-500
            `}
          >
            Submit
          </button>
        )}
      </form>
    </div>
  );
};

// ─── Waiting Survey Definitions ─────────────────────────────────────────────

interface WaitingSurveyConfig {
  surveyType: WaitingSurveyType;
  title: string;
  question: string;
  options: SurveyOption[];
  shuffleOptions: boolean;
}

/**
 * Survey definitions ordered by customer acquisition value.
 * Surveys are shown one at a time during fingering generation.
 */
const WAITING_SURVEYS: WaitingSurveyConfig[] = [
  {
    surveyType: 'pricing',
    title: 'Quick Survey',
    question:
      'How many times do you typically start practicing a new piece per month?',
    options: [
      { value: 'less-than-1', label: 'Less than 1' },
      { value: '1-5', label: '1~5' },
      { value: '5-10', label: '5~10' },
      { value: 'more-than-10', label: 'More than 10' },
    ],
    shuffleOptions: true,
  },
  {
    surveyType: 'discovery',
    title: 'Quick Survey',
    question: 'How did you hear about us?',
    options: [
      { value: 'search-engine', label: 'Search engine (Google, Bing, etc.)' },
      { value: 'youtube', label: 'YouTube' },
      { value: 'instagram', label: 'Instagram' },
      { value: 'tiktok', label: 'TikTok' },
      { value: 'reddit', label: 'Reddit' },
      { value: 'ai-chatbot', label: 'AI chatbot (ChatGPT, Claude, etc.)' },
      { value: 'word-of-mouth', label: 'Word of mouth' },
      { value: 'other', label: 'Other' },
    ],
    shuffleOptions: true,
  },
  {
    surveyType: 'online-community',
    title: 'Quick Survey',
    question: 'Which online communities do you participate in for piano?',
    options: [
      { value: 'reddit-piano', label: 'Reddit (r/piano, r/pianolearning)' },
      { value: 'facebook-groups', label: 'Facebook groups' },
      { value: 'discord', label: 'Discord servers' },
      { value: 'piano-forums', label: 'Piano forums (PianoWorld, etc.)' },
      { value: 'youtube-comments', label: 'YouTube comment sections' },
      { value: 'none', label: 'None' },
      { value: 'other', label: 'Other' },
    ],
    shuffleOptions: true,
  },
  {
    surveyType: 'reading-method',
    title: 'Quick Survey',
    question: 'What do you use to read sheet music during practice?',
    options: [
      { value: 'physical-books', label: 'Physical books' },
      { value: 'printed-sheets', label: 'Printed paper sheets' },
      { value: 'tablet', label: 'Tablet (iPad, etc.)' },
      { value: 'mobile-phone', label: 'Mobile phone' },
      { value: 'laptop-desktop', label: 'Laptop / desktop' },
      { value: 'other', label: 'Other' },
    ],
    shuffleOptions: true,
  },
  {
    surveyType: 'input-method',
    title: 'Quick Survey',
    question: 'What is the best way for you to input your music?',
    options: [
      { value: 'photo-mobile-tablet', label: 'Photo taken by mobile / tablet' },
      { value: 'musicxml', label: 'MusicXML file' },
      { value: 'pdf', label: 'PDF file' },
      { value: 'images', label: 'Images (scanned / screenshot)' },
      { value: 'audio', label: 'Audio file' },
      {
        value: 'online-video-link',
        label: 'Links to online performance videos',
      },
      { value: 'other', label: 'Other' },
    ],
    shuffleOptions: true,
  },
  {
    surveyType: 'music-source',
    title: 'Quick Survey',
    question: 'How do you usually find your sheet music?',
    options: [
      { value: 'digital-book-purchase', label: 'Digital book purchasing' },
      { value: 'physical-book-purchase', label: 'Physical book purchasing' },
      { value: 'imslp', label: 'IMSLP' },
      { value: 'free-scores', label: 'free-scores.com' },
      { value: 'musescore', label: 'Musescore' },
      { value: 'other-online-pdf', label: 'Other online PDF resources' },
      {
        value: 'audio-to-score',
        label: 'Audio-to-score transcription services',
      },
      { value: 'from-teacher', label: 'From my teacher' },
      { value: 'other', label: 'Other' },
    ],
    shuffleOptions: true,
  },
  {
    surveyType: 'genre',
    title: 'Quick Survey',
    question: 'What genre do you mostly play?',
    options: [
      { value: 'classical', label: 'Classical' },
      { value: 'pop-contemporary', label: 'Pop / contemporary' },
      { value: 'jazz', label: 'Jazz' },
      { value: 'film-game-music', label: 'Film / game music' },
      { value: 'hymns-worship', label: 'Hymns / worship' },
      { value: 'other', label: 'Other' },
    ],
    shuffleOptions: true,
  },
  {
    surveyType: 'role',
    title: 'Quick Survey',
    question: 'What best describes your role?',
    options: [
      { value: 'hobby-pianist', label: 'Hobby pianist' },
      { value: 'piano-student', label: 'Piano student' },
      { value: 'professional-pianist', label: 'Professional pianist' },
      { value: 'piano-teacher', label: 'Piano teacher' },
      { value: 'ai-tech-researcher', label: 'AI/tech researcher' },
      { value: 'other', label: 'Other' },
    ],
    shuffleOptions: true,
  },
  {
    surveyType: 'skill-level',
    title: 'Quick Survey',
    question: 'How would you describe your piano skill level?',
    options: [
      { value: 'beginner', label: 'Beginner (less than 2 years)' },
      { value: 'intermediate', label: 'Intermediate (2–5 years)' },
      { value: 'advanced', label: 'Advanced (5–10 years)' },
      { value: 'expert', label: 'Expert / conservatory level (10+ years)' },
    ],
    shuffleOptions: false,
  },
  {
    surveyType: 'practice-frequency',
    title: 'Quick Survey',
    question: 'How often do you practice piano?',
    options: [
      { value: 'daily', label: 'Daily' },
      { value: 'few-times-week', label: 'A few times a week' },
      { value: 'weekly', label: 'About once a week' },
      { value: 'few-times-month', label: 'A few times a month' },
      { value: 'rarely', label: 'Rarely' },
    ],
    shuffleOptions: false,
  },
  {
    surveyType: 'use-case',
    title: 'Quick Survey',
    question: 'When do you need this tool the most?',
    options: [
      { value: 'practice-for-fun', label: 'Practice for fun' },
      { value: 'performance-preparation', label: 'Performance preparation' },
      { value: 'teaching', label: 'Teaching' },
      { value: 'sight-reading', label: 'Sight-reading' },
      { value: 'other', label: 'Other' },
    ],
    shuffleOptions: true,
  },
];

// ─── Waiting Survey Carousel ────────────────────────────────────────────────

interface WaitingSurveyCarouselProps {
  answeredSurveyTypes: string[] | null; // null = not loaded yet
  isGenerating: boolean;
}

/**
 * Shows waiting surveys one at a time during fingering generation.
 * Auto-advances to next unanswered survey after each submission.
 * Hides when generation completes or all surveys are done.
 */
const WaitingSurveyCarousel = ({
  answeredSurveyTypes,
  isGenerating,
}: WaitingSurveyCarouselProps) => {
  // Rotation order follows WAITING_SURVEYS. Safe to filter with [] when null —
  // the render below bails out on null so nothing is asked before we know what
  // has already been answered.
  const unansweredSurveys = useMemo<WaitingSurveyConfig[]>(
    () =>
      WAITING_SURVEYS.filter(
        (s) => !(answeredSurveyTypes ?? []).includes(s.surveyType)
      ),
    [answeredSurveyTypes]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  // When generation completes, hide after a short delay
  useEffect(() => {
    if (!isGenerating) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isGenerating]);

  // Don't render until survey status has been loaded from backend
  if (answeredSurveyTypes === null) {
    return null;
  }

  if (
    !isVisible ||
    unansweredSurveys.length === 0 ||
    currentIndex >= unansweredSurveys.length
  ) {
    return null;
  }

  const currentSurvey = unansweredSurveys[currentIndex];

  const handleSurveyAnswered = () => {
    // Move to the next available survey
    setCurrentIndex((prev) => prev + 1);
  };

  return (
    <div className="flex w-full justify-center px-4">
      <GenericSurvey
        key={currentSurvey.surveyType}
        title={currentSurvey.title}
        question={currentSurvey.question}
        options={currentSurvey.options}
        surveyType={currentSurvey.surveyType}
        shuffleOptions={currentSurvey.shuffleOptions}
        autoSubmit={true}
        singleColumn={true}
        compact={true}
        onSurveyAnswered={handleSurveyAnswered}
      />
    </div>
  );
};

export { WaitingSurveyCarousel };
export type { SurveyOption, GenericSurveyProps, WaitingSurveyCarouselProps };
